import React, { useState, useCallback, useEffect } from 'react';
import {
  UploadCloud,
  FileCheck,
  AlertCircle,
  FileText,
  FileSpreadsheet,
  FileCode,
  Sparkles,
  CheckCircle2,
  Clock,
  Trash2,
  Eye,
  ShieldCheck,
  Lock,
  EyeOff,
  FileKey,
  PlusCircle,
  X,
  ExternalLink,
  Plus,
  AlertTriangle,
  Save,
  Check
} from 'lucide-react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase/config';
import { useGst } from '../context/GstContext';
import { InvoiceCategory, GstUploadMetadata, ExtractedGstInvoice } from '../types/gst';
import {
  computeFileSha256,
  extractInvoiceFromDocument,
  extractInvoicesFromSpreadsheet,
  parseGstr2bJson,
  persistExtractionToFirestore
} from '../services/extractionService';
import { validateAndCalculateInvoiceMath } from '../services/mathValidator';
import { classifyInvoiceCompliance } from '../services/complianceRagService';
import { resolveStatutoryTaxPeriod } from '../services/gstSystemInstructions';
import { toast, AlertBox } from '../components/ui';

const CATEGORIES: { key: InvoiceCategory; label: string; desc: string }[] = [
  {
    key: 'B2B_SALES',
    label: 'B2B Sales Invoices',
    desc: 'Outward invoices, GST sales register (Tally / Zoho / Busy)'
  },
  {
    key: 'B2B_PURCHASES',
    label: 'B2B Purchase Bills',
    desc: 'Vendor tax invoices for claiming Input Tax Credit'
  },
  {
    key: 'B2C_RETAIL',
    label: 'B2C Retail Summary',
    desc: 'Retail POS receipts & consumer sales summaries'
  },
  {
    key: 'EXPENSE_RECEIPT',
    label: 'Expense Receipts',
    desc: 'Foreign SaaS (AWS/OpenAI), fuel, travel, legal & director fees'
  },
  {
    key: 'GSTR_2B_JSON',
    label: 'GSTR-2B / 2A JSON',
    desc: 'Official GSTN JSON downloaded from Government GST Portal'
  }
];

interface ManualLineItem {
  description: string;
  hsn_sac_code: string;
  qty: number;
  unit_price: number;
  taxable_value: number;
  cgst_rate: number;
  sgst_rate: number;
  igst_rate: number;
}

export const UploadVaultPage: React.FC = () => {
  const {
    clientId,
    financialYear,
    taxPeriod,
    uploads,
    invoices,
    gstr2bRecords,
    setSecurityModalOpen,
    setPurgeModalOpen,
    privacyShieldMode,
    setPrivacyShieldMode,
    deleteUpload,
    refreshData,
    setFinancialYear,
    setTaxPeriod
  } = useGst();

  const [category, setCategory] = useState<InvoiceCategory>('B2B_PURCHASES');
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [progressText, setProgressText] = useState<string>('');
  const [progressPercent, setProgressPercent] = useState<number>(0);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'warn';
    message: string;
    failedFile?: File;
  } | null>(null);

  // Per-file deletion modal state
  const [fileToDelete, setFileToDelete] = useState<GstUploadMetadata | null>(null);
  const [isDeletingFile, setIsDeletingFile] = useState<boolean>(false);

  // Split-Screen Manual Ingestion Studio Modal State
  const [isManualModalOpen, setIsManualModalOpen] = useState<boolean>(false);
  const [manualFile, setManualFile] = useState<File | null>(null);
  const [manualPreviewUrl, setManualPreviewUrl] = useState<string>('');
  const [manualCategory, setManualCategory] = useState<InvoiceCategory>('B2B_PURCHASES');
  const [manualDocType, setManualDocType] = useState<string>('INVOICE');
  const [manualInvoiceNumber, setManualInvoiceNumber] = useState<string>('');
  const [manualInvoiceDate, setManualInvoiceDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [manualSupplierName, setManualSupplierName] = useState<string>('');
  const [manualSupplierGstin, setManualSupplierGstin] = useState<string>('');
  const [manualPlaceOfSupply, setManualPlaceOfSupply] = useState<string>('09 Uttar Pradesh');
  const [isZeroGst, setIsZeroGst] = useState<boolean>(false);
  const [manualLineItems, setManualLineItems] = useState<ManualLineItem[]>([
    { description: 'MCA Government Filing Fee Reimbursement', hsn_sac_code: '', qty: 1, unit_price: 900, taxable_value: 900, cgst_rate: 0, sgst_rate: 0, igst_rate: 0 }
  ]);
  const [isManualSaving, setIsManualSaving] = useState<boolean>(false);

  // Cleanup object URLs on unmount or file change
  useEffect(() => {
    return () => {
      if (manualPreviewUrl && manualPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(manualPreviewUrl);
      }
    };
  }, [manualPreviewUrl]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const openManualIngestionWithFile = (file?: File) => {
    if (file) {
      setManualFile(file);
      const blobUrl = URL.createObjectURL(file);
      setManualPreviewUrl(blobUrl);
      const cleanName = file.name.replace(/\.[^/.]+$/, "");
      setManualSupplierName(cleanName.replace(/_/g, ' '));
    }
    setManualCategory(category);
    setIsManualModalOpen(true);
  };

  const handleManualFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setManualFile(file);
      if (manualPreviewUrl && manualPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(manualPreviewUrl);
      }
      const blobUrl = URL.createObjectURL(file);
      setManualPreviewUrl(blobUrl);
    }
  };

  const handleZeroGstToggle = (checked: boolean) => {
    setIsZeroGst(checked);
    if (checked) {
      setManualDocType('DEBIT_NOTE');
      setManualLineItems(prev => prev.map(l => ({
        ...l,
        cgst_rate: 0,
        sgst_rate: 0,
        igst_rate: 0
      })));
    } else {
      setManualDocType('INVOICE');
      setManualLineItems(prev => prev.map(l => ({
        ...l,
        cgst_rate: 9,
        sgst_rate: 9,
        igst_rate: 0
      })));
    }
  };

  const addManualLineItem = () => {
    setManualLineItems(prev => [
      ...prev,
      {
        description: 'Line Item ' + (prev.length + 1),
        hsn_sac_code: isZeroGst ? '' : '9983',
        qty: 1,
        unit_price: 0,
        taxable_value: 0,
        cgst_rate: isZeroGst ? 0 : 9,
        sgst_rate: isZeroGst ? 0 : 9,
        igst_rate: 0
      }
    ]);
  };

  const removeManualLineItem = (index: number) => {
    if (manualLineItems.length <= 1) return;
    setManualLineItems(prev => prev.filter((_, idx) => idx !== index));
  };

  const updateManualLineItem = (index: number, field: keyof ManualLineItem, value: any) => {
    setManualLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'qty' || field === 'unit_price') {
        const qty = field === 'qty' ? Number(value) || 0 : updated[index].qty;
        const price = field === 'unit_price' ? Number(value) || 0 : updated[index].unit_price;
        updated[index].taxable_value = Number((qty * price).toFixed(2));
      }
      return updated;
    });
  };

  // Compute live preview totals for manual modal
  const computedTaxable = manualLineItems.reduce((acc, l) => acc + (Number(l.taxable_value) || 0), 0);
  const computedCgst = isZeroGst ? 0 : manualLineItems.reduce((acc, l) => acc + ((Number(l.taxable_value) || 0) * (Number(l.cgst_rate) || 0) / 100), 0);
  const computedSgst = isZeroGst ? 0 : manualLineItems.reduce((acc, l) => acc + ((Number(l.taxable_value) || 0) * (Number(l.sgst_rate) || 0) / 100), 0);
  const computedIgst = isZeroGst ? 0 : manualLineItems.reduce((acc, l) => acc + ((Number(l.taxable_value) || 0) * (Number(l.igst_rate) || 0) / 100), 0);
  const computedTotalTax = Number((computedCgst + computedSgst + computedIgst).toFixed(2));
  const computedGrandTotal = Number((computedTaxable + computedTotalTax).toFixed(2));

  const handleSaveManualIngestion = async () => {
    if (!clientId) {
      toast.error('Client context not found. Please log in.');
      return;
    }
    if (!manualInvoiceNumber.trim()) {
      toast.error('Please enter an Invoice or Document Number');
      return;
    }
    if (!manualSupplierName.trim()) {
      toast.error('Please enter the Supplier / Party Name');
      return;
    }

    setIsManualSaving(true);
    try {
      let downloadUrl = "";
      let storagePath = "manual_entry";
      let sha256 = `manual_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
      let fileName = manualFile ? manualFile.name : `Manual_Entry_${manualInvoiceNumber}.pdf`;
      let fileSize = manualFile ? manualFile.size : 0;
      let fileType = manualFile ? (manualFile.type || 'application/pdf') : 'manual/input';

      // 1. Upload manual file proof to Firebase Storage if present
      if (manualFile) {
        sha256 = await computeFileSha256(manualFile);
        const sanitizedName = manualFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        storagePath = `clients/${clientId}/gst/${financialYear}/${taxPeriod}/${manualCategory}/${Date.now()}_${sanitizedName}`;
        const storageReference = ref(storage, storagePath);
        try {
          const uploadTask = await uploadBytesResumable(storageReference, manualFile);
          downloadUrl = await getDownloadURL(uploadTask.ref);
        } catch (uploadErr) {
          console.warn("Storage upload warn:", uploadErr);
        }
      }

      // 2. Build structured invoice payload and run deterministic math
      const rawPayload = {
        supplier_gstin: manualSupplierGstin.trim().toUpperCase(),
        supplier_name: manualSupplierName.trim(),
        buyer_gstin: "",
        invoice_number: manualInvoiceNumber.trim(),
        invoice_date: manualInvoiceDate,
        place_of_supply: manualPlaceOfSupply,
        reverse_charge_applicable: false,
        invoice_total: computedGrandTotal,
        is_reimbursement_or_pure_agent: isZeroGst || computedTotalTax === 0,
        document_type: manualDocType,
        line_items: manualLineItems.map(item => ({
          description: item.description,
          hsn_sac_code: item.hsn_sac_code,
          qty: Number(item.qty) || 1,
          unit_price: Number(item.unit_price) || Number(item.taxable_value) || 0,
          taxable_value: Number(item.taxable_value) || 0,
          cgst_rate: isZeroGst ? 0 : Number(item.cgst_rate) || 0,
          sgst_rate: isZeroGst ? 0 : Number(item.sgst_rate) || 0,
          igst_rate: isZeroGst ? 0 : Number(item.igst_rate) || 0,
          cess_rate: 0
        }))
      };

      const math = validateAndCalculateInvoiceMath(rawPayload as any);
      const compliance = classifyInvoiceCompliance({
        ...rawPayload,
        category: manualCategory,
        line_items: math.verifiedLineItems
      } as any);

      const resolvedPeriod = resolveStatutoryTaxPeriod(manualInvoiceDate);
      const targetFy = resolvedPeriod.financialYear;
      const targetMonth = resolvedPeriod.taxPeriod;

      const invoiceId = `inv_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
      const uploadId = `up_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

      const newInvoice: ExtractedGstInvoice = {
        id: invoiceId,
        clientId,
        financialYear: targetFy,
        taxPeriod: targetMonth,
        category: manualCategory,
        supplier_gstin: manualSupplierGstin.trim().toUpperCase(),
        supplier_name: manualSupplierName.trim(),
        buyer_gstin: "",
        invoice_number: manualInvoiceNumber.trim(),
        normalized_invoice_number: math.normalizedInvoiceNumber,
        invoice_date: manualInvoiceDate,
        place_of_supply: manualPlaceOfSupply,
        reverse_charge_applicable: compliance.reverseChargeApplicable,
        is_reimbursement_or_pure_agent: isZeroGst || math.totalTax === 0,
        document_type: manualDocType,
        line_items: math.verifiedLineItems,
        total_taxable_value: math.totalTaxableValue,
        total_tax: math.totalTax,
        invoice_total: math.calculatedInvoiceTotal,
        math_verified: math.mathVerified,
        itc_eligibility: compliance.itcEligibility,
        ai_compliance_note: compliance.aiComplianceNote,
        recon_status: 'UNMATCHED',
        source_file_path: storagePath,
        source_file_url: downloadUrl,
        created_at: new Date().toISOString(),
        audit_trail: [
          {
            action: 'AI_FALLBACK_MANUAL_ENTRY',
            timestamp: new Date().toISOString(),
            userId: clientId,
            details: `User verified and entered document details via Split-Screen Ingestion Studio. Math Verified: ${math.mathVerified}. File proof linked: ${manualFile ? 'YES' : 'NO'}`
          }
        ]
      };

      const uploadMeta: GstUploadMetadata = {
        id: uploadId,
        clientId,
        financialYear: targetFy,
        taxPeriod: targetMonth,
        category: manualCategory,
        fileName,
        fileSize,
        fileType,
        storagePath,
        downloadUrl,
        sha256Hash: sha256,
        extractedCount: 1,
        status: 'COMPLETED',
        uploadedBy: clientId,
        uploadedAt: new Date().toISOString()
      };

      await persistExtractionToFirestore(clientId, [newInvoice], [], uploadMeta);
      await refreshData();

      setFinancialYear(targetFy);
      setTaxPeriod(targetMonth);
      toast.success(`Invoice ${manualInvoiceNumber} filed under ${targetMonth} ${targetFy} and linked to document proof!`);
      setIsManualModalOpen(false);
      setManualFile(null);
      setManualPreviewUrl('');
    } catch (err: any) {
      console.error('Failed to save manual ingestion:', err);
      toast.error('Failed to save invoice: ' + (err?.message || 'Unknown error'));
    } finally {
      setIsManualSaving(false);
    }
  };

  const handleDeleteFileConfirm = async () => {
    if (!fileToDelete) return;
    setIsDeletingFile(true);
    try {
      await deleteUpload(fileToDelete.id, fileToDelete.storagePath);
      toast.success(`File "${fileToDelete.fileName}" and associated ledger records permanently deleted.`);
      setFileToDelete(null);
    } catch (err: any) {
      console.error('Delete file error:', err);
      toast.error('Failed to delete file: ' + (err?.message || 'Unknown error'));
    } finally {
      setIsDeletingFile(false);
    }
  };

  const processFile = async (file: File) => {
    if (!clientId) {
      setNotification({ type: 'error', message: 'Please ensure client context is loaded' });
      return;
    }

    setNotification(null);
    setIsProcessing(true);
    setProgressPercent(10);
    setProgressText(`Computing SHA-256 hash for ${file.name}...`);

    try {
      // 1. Compute SHA-256 hash
      const sha256 = await computeFileSha256(file);

      // Check for duplicate upload in the same period
      const isDuplicate = uploads.some(
        u => u.sha256Hash === sha256 && u.financialYear === financialYear && u.taxPeriod === taxPeriod
      );

      if (isDuplicate) {
        setNotification({
          type: 'warn',
          message: `Duplicate detected: "${file.name}" has already been ingested for ${taxPeriod} ${financialYear}.`
        });
        setIsProcessing(false);
        return;
      }

      setProgressPercent(30);
      setProgressText(`Uploading to secure storage: clients/${clientId}/gst/...`);

      // 2. Upload to Firebase Storage
      const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const storagePath = `clients/${clientId}/gst/${financialYear}/${taxPeriod}/${category}/${Date.now()}_${sanitizedName}`;
      const storageReference = ref(storage, storagePath);

      let downloadUrl = "";
      try {
        const uploadTask = await uploadBytesResumable(storageReference, file);
        downloadUrl = await getDownloadURL(uploadTask.ref);
      } catch (uploadErr) {
        console.warn("Storage upload warning (proceeding with local buffer if offline):", uploadErr);
      }

      setProgressPercent(60);
      setProgressText(`Running AI extraction and deterministic math validation...`);

      const uploadId = `up_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
      const uploadMeta: GstUploadMetadata = {
        id: uploadId,
        clientId,
        financialYear,
        taxPeriod,
        category,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type || file.name.split('.').pop() || 'unknown',
        storagePath,
        downloadUrl,
        sha256Hash: sha256,
        extractedCount: 0,
        status: 'PROCESSING',
        uploadedBy: clientId,
        uploadedAt: new Date().toISOString()
      };

      let extractedInvoices: any[] = [];
      let records2b: any[] = [];

      const fileExt = file.name.split('.').pop()?.toLowerCase();

      // 3. Trigger Extraction based on file type
      if (category === 'GSTR_2B_JSON' || fileExt === 'json') {
        records2b = await parseGstr2bJson(file, clientId, financialYear, taxPeriod, storagePath);
      } else if (fileExt === 'xlsx' || fileExt === 'xls' || fileExt === 'csv') {
        extractedInvoices = await extractInvoicesFromSpreadsheet(
          file, clientId, financialYear, taxPeriod, category, storagePath, downloadUrl
        );
      } else {
        // PDF or Image via Gemini 3.8 Flash Vision
        const singleInvoice = await extractInvoiceFromDocument(
          file, clientId, financialYear, taxPeriod, category, storagePath, downloadUrl
        );
        extractedInvoices = [singleInvoice];
      }

      // 4. Apply Client-Side High-Privacy Redaction Shield if enabled
      if (privacyShieldMode === 'REDACTED') {
        extractedInvoices = extractedInvoices.map((inv, idx) => ({
          ...inv,
          supplier_name: inv.supplier_name
            ? `Vendor_***${(inv.supplier_gstin ? inv.supplier_gstin.slice(-4) : (idx + 1).toString().padStart(2, '0'))}`
            : 'Vendor_Redacted',
          audit_trail: [
            ...(inv.audit_trail || []),
            {
              action: 'PII_CLIENT_REDACTED',
              timestamp: new Date().toISOString(),
              userId: clientId,
              details: 'High-Privacy Redaction Shield Active: Supplier trade name tokenized and masked client-side before persistence.'
            }
          ]
        }));
      }

      setProgressPercent(90);
      setProgressText(`Persisting ${extractedInvoices.length + records2b.length} records to tenant Firestore...`);

      // 5. Batch Persist to Firestore
      const targetFy = extractedInvoices[0]?.financialYear || uploadMeta.financialYear;
      const targetMonth = extractedInvoices[0]?.taxPeriod || uploadMeta.taxPeriod;
      if (extractedInvoices.length > 0 && extractedInvoices[0].financialYear) {
        uploadMeta.financialYear = extractedInvoices[0].financialYear;
        uploadMeta.taxPeriod = extractedInvoices[0].taxPeriod;
      }

      await persistExtractionToFirestore(clientId, extractedInvoices, records2b, uploadMeta);
      await refreshData();

      // Automatically switch the active workspace period to match the ingested invoice
      if (targetFy && targetMonth && (targetFy !== financialYear || targetMonth !== taxPeriod)) {
        setFinancialYear(targetFy);
        setTaxPeriod(targetMonth);
      }

      setProgressPercent(100);
      const isZeroTax = extractedInvoices.some(i => i.total_tax === 0);
      const zeroTaxNote = isZeroTax ? ' (Zero-GST / Reimbursement detected: ₹0 tax & ₹0 ITC recorded).' : '';
      const successMsg = `Successfully processed "${file.name}"! Extracted ${extractedInvoices.length} invoices and filed into ${targetMonth} ${targetFy}.${zeroTaxNote}${privacyShieldMode === 'REDACTED' ? ' (High-Privacy PII Masking Applied)' : ''}`;
      
      setNotification({
        type: 'success',
        message: successMsg
      });
      toast.success(successMsg);
    } catch (err: any) {
      console.error("Upload error:", err);
      const errMsg = `Extraction Alert: ${err.message || 'Unable to parse document structure'}`;
      setNotification({
        type: 'error',
        message: errMsg,
        failedFile: file
      });
      toast.error(errMsg);
    } finally {
      setIsProcessing(false);
      setProgressPercent(0);
      setProgressText('');
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFile(e.target.files[0]);
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Page Title & Scope */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <UploadCloud className="h-6 w-6 text-cyan-400" />
            <span>Multi-Format Ingestion Vault</span>
          </h1>
          <p className="text-xs text-slate-300 mt-0.5">
            Tenant-isolated intake for invoices, books, receipts, and GSTR-2B JSON with client-side SHA-256 deduplication
          </p>
        </div>
        <div className="flex items-center gap-2.5">
          <button
            onClick={() => openManualIngestionWithFile()}
            className="flex items-center gap-2 px-3.5 py-1.5 rounded-xl border border-cyan-500/50 bg-cyan-950/40 hover:bg-cyan-900/60 text-cyan-300 font-semibold text-xs transition shadow-sm"
          >
            <PlusCircle className="h-4 w-4 text-cyan-400" />
            <span>Manual Ingestion (Split View)</span>
          </button>
          <div className="rounded-xl border border-cyan-500/30 bg-cyan-950/30 px-3.5 py-1.5 text-xs text-cyan-300 font-medium shadow-sm">
            Active Target: <span className="font-bold text-white">{taxPeriod} {financialYear}</span>
          </div>
          <button
            onClick={() => setSecurityModalOpen(true)}
            className="flex items-center gap-1.5 rounded-xl border border-emerald-500/40 bg-emerald-950/30 px-3 py-1.5 text-xs text-emerald-300 font-medium hover:bg-emerald-950/60 transition shadow-sm"
          >
            <ShieldCheck className="h-4 w-4 text-emerald-400" />
            <span>Data Shield</span>
          </button>
        </div>
      </div>

      {/* Enterprise Privacy Shield Banner & Toggle */}
      <div className="rounded-2xl border border-white/10 bg-[#12141a] p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${
            privacyShieldMode === 'REDACTED'
              ? 'bg-purple-500/10 border border-purple-500/30 text-purple-400'
              : 'bg-cyan-500/10 border border-cyan-500/30 text-cyan-400'
          }`}>
            {privacyShieldMode === 'REDACTED' ? <EyeOff className="h-5 w-5" /> : <Lock className="h-5 w-5" />}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-white">Client-Side Redaction & Privacy Shield</span>
              <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded border uppercase ${
                privacyShieldMode === 'REDACTED'
                  ? 'bg-purple-500/20 text-purple-300 border-purple-500/40'
                  : 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40'
              }`}>
                {privacyShieldMode === 'REDACTED' ? 'High-Privacy Redacted' : 'Standard Statutory'}
              </span>
            </div>
            <p className="text-[11px] text-slate-300 mt-0.5 max-w-2xl leading-relaxed">
              {privacyShieldMode === 'REDACTED'
                ? '🔒 In-Browser Masking Active: Vendor company names are pseudonymized (e.g. Vendor_***42) before cloud storage & AI extraction. Only statutory tax math (GSTIN, HSN, Taxable Value, CGST/SGST/IGST) is stored.'
                : 'Extracts full legal invoice fields including supplier trade names for direct statutory filing. Guarded by AES-256 and Zero AI Model Retention.'}
            </p>
          </div>
        </div>

        {/* Toggle Buttons */}
        <div className="flex items-center gap-1.5 bg-[#0b0c0e] p-1 rounded-xl border border-white/10 shrink-0 self-end md:self-auto">
          <button
            onClick={() => setPrivacyShieldMode('STANDARD')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition border ${
              privacyShieldMode === 'STANDARD'
                ? 'bg-cyan-600 text-black border-cyan-500 shadow-sm'
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
          >
            Standard Mode
          </button>
          <button
            onClick={() => setPrivacyShieldMode('REDACTED')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition border ${
              privacyShieldMode === 'REDACTED'
                ? 'bg-purple-600 text-white border-purple-500 shadow-sm'
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
          >
            <EyeOff className="h-3.5 w-3.5" />
            <span>Redacted Mode</span>
          </button>
        </div>
      </div>

      {notification && (
        <div className="space-y-2">
          <AlertBox
            variant={
              notification.type === 'success'
                ? 'success'
                : notification.type === 'warn'
                ? 'warning'
                : 'error'
            }
            title={
              notification.type === 'success'
                ? 'Upload Verified'
                : notification.type === 'warn'
                ? 'Upload Notice'
                : 'Extraction Alert'
            }
            message={notification.message}
            onDismiss={() => setNotification(null)}
          />
          {notification.failedFile && (
            <div className="flex items-center justify-between p-3 rounded-xl border border-cyan-500/40 bg-cyan-950/30 text-xs">
              <span className="text-slate-300">
                Would you like to manually verify and record this document using the side-by-side split screen?
              </span>
              <button
                onClick={() => openManualIngestionWithFile(notification.failedFile)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-black font-bold text-xs transition"
              >
                <PlusCircle className="h-3.5 w-3.5" />
                <span>Open Split-Screen Manual Entry</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Category Selector Grid */}
      <div className="space-y-2">
        <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">
          Step 1: Select Invoice Category
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.key}
              onClick={() => setCategory(cat.key)}
              className={`p-3 rounded-xl border text-left transition ${
                category === cat.key
                  ? 'bg-cyan-500/15 border-cyan-500/50 text-white shadow-sm ring-1 ring-cyan-500/30'
                  : 'bg-[#14161f] border-white/10 text-slate-300 hover:bg-white/[0.04] hover:text-white hover:border-cyan-500/30'
              }`}
            >
              <div className="font-semibold text-xs mb-1 text-white">{cat.label}</div>
              <div className="text-[11px] text-slate-400 leading-snug">{cat.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Drag & Drop Upload Zone */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            Step 2: Upload Files (.pdf, .jpg, .png, .xlsx, .csv, .json)
          </label>
          <button
            type="button"
            onClick={() => openManualIngestionWithFile()}
            className="text-xs text-cyan-400 hover:underline flex items-center gap-1 font-medium"
          >
            <PlusCircle className="h-3.5 w-3.5" /> Or enter manually with split-screen proof
          </button>
        </div>
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`relative flex flex-col items-center justify-center rounded-2xl border-2 border-dashed p-10 text-center transition ${
            isDragging
              ? 'border-cyan-400 bg-cyan-950/30'
              : 'border-white/15 bg-[#12141a] hover:border-cyan-400/50'
          }`}
        >
          <input
            type="file"
            onChange={handleFileInput}
            accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.csv,.json"
            className="absolute inset-0 opacity-0 cursor-pointer"
            disabled={isProcessing}
          />
          <div className="p-3.5 rounded-2xl bg-white/[0.05] text-cyan-400 mb-3 border border-white/10">
            <UploadCloud className="h-8 w-8" />
          </div>
          <p className="text-sm font-semibold text-white">
            Drag and drop invoices or spreadsheet register here
          </p>
          <p className="text-xs text-slate-300 mt-1 max-w-md leading-relaxed">
            Directly extracts structured line items using Gemini 3.8 Flash Vision & parses Tally/Zoho Excel dumps with zero mock fallbacks.
          </p>

          <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-[10px] text-slate-300">
            <span className="flex items-center gap-1 rounded-lg bg-black/50 px-2.5 py-1 border border-white/10 font-medium">
              <FileText className="h-3 w-3 text-red-400" /> PDF / Scan
            </span>
            <span className="flex items-center gap-1 rounded-lg bg-black/50 px-2.5 py-1 border border-white/10 font-medium">
              <FileSpreadsheet className="h-3 w-3 text-emerald-400" /> Excel / CSV
            </span>
            <span className="flex items-center gap-1 rounded-lg bg-black/50 px-2.5 py-1 border border-white/10 font-medium">
              <FileCode className="h-3 w-3 text-blue-400" /> GSTR-2B JSON
            </span>
          </div>
        </div>
      </div>

      {/* Active Processing Indicator */}
      {isProcessing && (
        <div className="rounded-xl border border-cyan-500/30 bg-[#131316] p-4 text-xs space-y-2">
          <div className="flex items-center justify-between font-medium text-cyan-300">
            <span className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 animate-spin" />
              {progressText}
            </span>
            <span>{progressPercent}%</span>
          </div>
          <div className="h-1.5 w-full bg-black/60 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}

      {/* Vault Ingestion History & Purge Controls */}
      <div className="space-y-3 pt-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            Ingested Vault Files ({uploads.length})
          </h3>
          {uploads.length > 0 && (
            <button
              onClick={() => setPurgeModalOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1 rounded-xl border border-rose-500/30 bg-rose-950/20 hover:bg-rose-950/40 text-rose-300 text-xs font-medium transition"
            >
              <Trash2 className="h-3.5 w-3.5" />
              <span>Purge Period Data</span>
            </button>
          )}
        </div>

        {uploads.length === 0 ? (
          <div className="rounded-xl border border-white/10 bg-[#131316] p-8 text-center text-xs text-slate-400">
            No files ingested yet for {taxPeriod} {financialYear}. Drag and drop vendor bills or use Manual Ingestion above.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {uploads.map((u) => (
              <div
                key={u.id}
                className="rounded-xl border border-white/15 bg-[#14161f] p-3.5 space-y-2 text-xs shadow-sm hover:border-white/25 transition"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 truncate">
                    <FileCheck className="h-4 w-4 text-cyan-400 shrink-0" />
                    <span className="font-semibold text-white truncate" title={u.fileName}>{u.fileName}</span>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="rounded bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-bold text-emerald-400 border border-emerald-500/20">
                      {u.status}
                    </span>
                    <button
                      onClick={() => setFileToDelete(u)}
                      className="p-1 rounded text-rose-400 hover:text-rose-300 hover:bg-rose-950/40 border border-transparent hover:border-rose-500/30 transition"
                      title="Delete uploaded file and its extracted records"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-1 text-[11px] text-slate-400">
                  <div>Category: <span className="text-slate-200">{u.category}</span></div>
                  <div>Extracted: <span className="text-cyan-300 font-bold">{u.extractedCount} records</span></div>
                  <div>Period: <span className="text-slate-200">{u.taxPeriod}</span></div>
                  <div>Size: <span className="text-slate-200">{(u.fileSize / 1024).toFixed(1)} KB</span></div>
                </div>

                <div className="pt-1 border-t border-white/5 text-[9px] font-mono text-slate-400 truncate flex items-center justify-between">
                  <span>SHA-256: {u.sha256Hash.slice(0, 16)}...</span>
                  {u.downloadUrl && (
                    <a
                      href={u.downloadUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-cyan-400 hover:underline flex items-center gap-0.5 font-sans"
                    >
                      <ExternalLink className="h-2.5 w-2.5" /> File
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Uploaded File Confirmation Modal */}
      {fileToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="relative w-full max-w-md rounded-2xl border border-rose-500/30 bg-[#131316] p-6 shadow-2xl space-y-4">
            <div className="flex items-center gap-3 text-rose-400">
              <div className="p-2.5 rounded-xl bg-rose-950/50 border border-rose-500/40">
                <Trash2 className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-white">Delete File & Invoices</h3>
                <p className="text-xs text-slate-400 font-mono truncate max-w-[240px]">{fileToDelete.fileName}</p>
              </div>
            </div>

            <p className="text-xs text-slate-300 leading-relaxed">
              Are you sure you want to permanently delete <span className="font-bold text-white">{fileToDelete.fileName}</span>?
            </p>
            <p className="text-[11px] text-slate-400 bg-black/40 p-3 rounded-xl border border-white/5">
              ⚠️ This will remove the file from cloud storage and remove all <span className="text-cyan-300 font-bold">{fileToDelete.extractedCount}</span> invoice record(s) linked to this document from your ledger and GST returns.
            </p>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setFileToDelete(null)}
                disabled={isDeletingFile}
                className="px-4 py-2 rounded-xl border border-white/10 bg-white/5 text-slate-300 hover:bg-white/10 text-xs font-semibold transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteFileConfirm}
                disabled={isDeletingFile}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-xs font-bold transition shadow-sm shadow-rose-600/30"
              >
                <Trash2 className="h-3.5 w-3.5" />
                <span>{isDeletingFile ? 'Deleting...' : 'Confirm Delete'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Split-Screen Manual Ingestion Studio Modal */}
      {isManualModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-md p-4 overflow-y-auto">
          <div className="relative w-full max-w-6xl rounded-2xl border border-white/15 bg-[#131316] shadow-2xl flex flex-col max-h-[92vh]">
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-white/10 px-6 py-4 bg-[#0e1015]">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-cyan-950 border border-cyan-500/30 text-cyan-400">
                  <FileText className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white flex items-center gap-2">
                    <span>Split-Screen Manual Ingestion Studio</span>
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded border border-cyan-500/30 bg-cyan-950/40 text-cyan-300 uppercase">
                      Audit Linked
                    </span>
                  </h3>
                  <p className="text-xs text-slate-400">
                    Review source document proof on the left & manually enter line items with verified Decimal.js math
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setIsManualModalOpen(false);
                  setManualFile(null);
                  setManualPreviewUrl('');
                }}
                className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-white/5 transition"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Split Content: Left Document Preview, Right Manual Form */}
            <div className="flex-1 overflow-y-auto grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-white/10">
              {/* LEFT PANE: Document Proof Viewer */}
              <div className="p-5 flex flex-col bg-[#0b0c0e] space-y-3 min-h-[400px]">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                    <FileKey className="h-3.5 w-3.5 text-cyan-400" />
                    Source Document Proof
                  </span>
                  <label className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-cyan-300 text-xs font-medium cursor-pointer transition">
                    <Plus className="h-3.5 w-3.5" />
                    <span>{manualFile ? 'Change File' : 'Attach Document'}</span>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleManualFileChange}
                      className="hidden"
                    />
                  </label>
                </div>

                <div className="flex-1 rounded-xl border border-white/10 bg-[#12141a] overflow-hidden flex items-center justify-center min-h-[450px]">
                  {manualPreviewUrl ? (
                    manualFile?.type?.includes('image') || manualPreviewUrl.match(/\.(jpg|jpeg|png)$/i) ? (
                      <div className="w-full h-full p-2 flex items-center justify-center overflow-auto max-h-[550px]">
                        <img
                          src={manualPreviewUrl}
                          alt="Document proof preview"
                          className="max-h-[520px] max-w-full object-contain rounded"
                        />
                      </div>
                    ) : (
                      <iframe
                        src={manualPreviewUrl}
                        title="Document proof preview"
                        className="w-full h-full min-h-[520px] border-0"
                      />
                    )
                  ) : (
                    <div className="text-center p-8 space-y-3 text-slate-400">
                      <FileText className="h-12 w-12 text-slate-600 mx-auto" />
                      <div>
                        <p className="text-sm font-semibold text-slate-300">No document attached yet</p>
                        <p className="text-xs text-slate-500 mt-1">
                          Attach a PDF or photo of the invoice/debit note to link it as cryptographic audit proof.
                        </p>
                      </div>
                      <label className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-black font-bold text-xs cursor-pointer transition">
                        <UploadCloud className="h-4 w-4" />
                        <span>Select Document</span>
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleManualFileChange}
                          className="hidden"
                        />
                      </label>
                    </div>
                  )}
                </div>

                {manualFile && (
                  <div className="flex items-center justify-between text-[11px] text-slate-400 px-1 font-mono">
                    <span className="truncate max-w-[280px]">{manualFile.name}</span>
                    <span>{(manualFile.size / 1024).toFixed(1)} KB</span>
                  </div>
                )}
              </div>

              {/* RIGHT PANE: Manual Form & Deterministic Math */}
              <div className="p-6 space-y-4 overflow-y-auto">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">
                    Statutory Accounting Fields
                  </h4>
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2 cursor-pointer bg-white/[0.04] px-3 py-1.5 rounded-xl border border-white/10 hover:border-cyan-500/40 transition">
                      <input
                        type="checkbox"
                        checked={isZeroGst}
                        onChange={(e) => handleZeroGstToggle(e.target.checked)}
                        className="rounded text-cyan-500 focus:ring-0 focus:outline-none cursor-pointer"
                      />
                      <span className="text-xs font-semibold text-slate-200">
                        Zero-GST / Reimbursement Note
                      </span>
                    </label>
                  </div>
                </div>

                {isZeroGst && (
                  <div className="rounded-xl border border-emerald-500/30 bg-emerald-950/20 p-3 text-xs space-y-1">
                    <div className="font-bold text-emerald-300 flex items-center gap-1.5">
                      <ShieldCheck className="h-4 w-4" />
                      Zero-GST / Pure Agent Reimbursement Active
                    </div>
                    <p className="text-slate-300 text-[11px]">
                      Tax rates are locked at 0.00% under Rule 33 of CGST Rules (e.g. government/MCA fee reimbursements). No tax liability or ITC is accumulated.
                    </p>
                  </div>
                )}

                {/* Primary Metadata Form */}
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <label className="text-[10px] text-slate-400 font-bold uppercase">Category</label>
                    <select
                      value={manualCategory}
                      onChange={(e) => setManualCategory(e.target.value as InvoiceCategory)}
                      className="w-full rounded-xl border border-white/10 bg-[#0e1015] px-3 py-2 text-white focus:outline-none focus:border-cyan-500"
                    >
                      <option value="B2B_PURCHASES">B2B Purchase Bill (Inward)</option>
                      <option value="B2B_SALES">B2B Sales Invoice (Outward)</option>
                      <option value="EXPENSE_RECEIPT">Expense / Reimbursement</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-[10px] text-slate-400 font-bold uppercase">Document Type</label>
                    <select
                      value={manualDocType}
                      onChange={(e) => setManualDocType(e.target.value)}
                      className="w-full rounded-xl border border-white/10 bg-[#0e1015] px-3 py-2 text-white focus:outline-none focus:border-cyan-500 font-mono"
                    >
                      <option value="INVOICE">Tax Invoice</option>
                      <option value="DEBIT_NOTE">Debit Note</option>
                      <option value="CREDIT_NOTE">Credit Note</option>
                      <option value="BILL_OF_SUPPLY">Bill of Supply / Reimbursement</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-[10px] text-slate-400 font-bold uppercase">Invoice / Document # *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. DN/UR/26-27/2659"
                      value={manualInvoiceNumber}
                      onChange={(e) => setManualInvoiceNumber(e.target.value)}
                      className="w-full rounded-xl border border-white/10 bg-[#0e1015] px-3 py-2 font-mono text-white focus:outline-none focus:border-cyan-500"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] text-slate-400 font-bold uppercase">Document Date *</label>
                      <span className="text-[9px] font-mono text-cyan-300 font-semibold">
                        {resolveStatutoryTaxPeriod(manualInvoiceDate).taxPeriod} {resolveStatutoryTaxPeriod(manualInvoiceDate).financialYear}
                      </span>
                    </div>
                    <input
                      type="date"
                      required
                      value={manualInvoiceDate}
                      onChange={(e) => setManualInvoiceDate(e.target.value)}
                      className="w-full rounded-xl border border-white/10 bg-[#0e1015] px-3 py-2 font-mono text-white focus:outline-none focus:border-cyan-500"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] text-slate-400 font-bold uppercase">Supplier / Party Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Startup Movers"
                      value={manualSupplierName}
                      onChange={(e) => setManualSupplierName(e.target.value)}
                      className="w-full rounded-xl border border-white/10 bg-[#0e1015] px-3 py-2 text-white focus:outline-none focus:border-cyan-500"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] text-slate-400 font-bold uppercase">Supplier GSTIN (15 Chars)</label>
                    <input
                      type="text"
                      maxLength={15}
                      placeholder="e.g. 09AAUCS0938E1ZZ"
                      value={manualSupplierGstin}
                      onChange={(e) => setManualSupplierGstin(e.target.value.toUpperCase())}
                      className="w-full rounded-xl border border-white/10 bg-[#0e1015] px-3 py-2 font-mono text-white focus:outline-none focus:border-cyan-500 uppercase"
                    />
                  </div>
                </div>

                {/* Line Items Table */}
                <div className="space-y-2 pt-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-bold text-white">Line Items ({manualLineItems.length})</span>
                    <button
                      type="button"
                      onClick={addManualLineItem}
                      className="text-cyan-400 hover:text-cyan-300 flex items-center gap-1 font-semibold text-[11px]"
                    >
                      <Plus className="h-3 w-3" /> Add Item
                    </button>
                  </div>

                  <div className="rounded-xl border border-white/10 overflow-hidden text-xs">
                    <table className="w-full text-left font-mono">
                      <thead className="bg-[#0e1015] text-[10px] text-slate-400 uppercase">
                        <tr>
                          <th className="p-2">Description</th>
                          <th className="p-2 w-20">HSN/SAC</th>
                          <th className="p-2 w-24 text-right">Taxable (₹)</th>
                          <th className="p-2 w-24 text-right">GST Rate</th>
                          <th className="p-2 w-10"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {manualLineItems.map((line, idx) => (
                          <tr key={idx} className="hover:bg-white/[0.02]">
                            <td className="p-1.5 font-sans">
                              <input
                                type="text"
                                value={line.description}
                                onChange={(e) => updateManualLineItem(idx, 'description', e.target.value)}
                                className="w-full bg-transparent px-1.5 py-1 text-white border border-transparent hover:border-white/10 focus:border-cyan-500 rounded text-xs"
                              />
                            </td>
                            <td className="p-1.5">
                              <input
                                type="text"
                                placeholder="9983"
                                value={line.hsn_sac_code}
                                onChange={(e) => updateManualLineItem(idx, 'hsn_sac_code', e.target.value)}
                                className="w-full bg-transparent px-1.5 py-1 text-cyan-300 border border-transparent hover:border-white/10 focus:border-cyan-500 rounded text-xs"
                              />
                            </td>
                            <td className="p-1.5 text-right">
                              <input
                                type="number"
                                step="0.01"
                                value={line.taxable_value}
                                onChange={(e) => updateManualLineItem(idx, 'taxable_value', e.target.value)}
                                className="w-full bg-transparent px-1.5 py-1 text-right text-white border border-transparent hover:border-white/10 focus:border-cyan-500 rounded text-xs font-mono"
                              />
                            </td>
                            <td className="p-1.5 text-right">
                              <select
                                disabled={isZeroGst}
                                value={isZeroGst ? 0 : (line.cgst_rate + line.sgst_rate + line.igst_rate)}
                                onChange={(e) => {
                                  const rate = Number(e.target.value) || 0;
                                  updateManualLineItem(idx, 'cgst_rate', rate / 2);
                                  updateManualLineItem(idx, 'sgst_rate', rate / 2);
                                  updateManualLineItem(idx, 'igst_rate', 0);
                                }}
                                className="bg-[#0e1015] border border-white/10 rounded px-1.5 py-1 text-xs text-slate-300 focus:outline-none focus:border-cyan-500 disabled:opacity-50"
                              >
                                <option value={0}>0% (Nil/Exempt)</option>
                                <option value={5}>5%</option>
                                <option value={12}>12%</option>
                                <option value={18}>18%</option>
                                <option value={28}>28%</option>
                              </select>
                            </td>
                            <td className="p-1.5 text-center">
                              {manualLineItems.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeManualLineItem(idx)}
                                  className="text-slate-500 hover:text-rose-400 p-1"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Mathematical Summary Card */}
                <div className="rounded-xl border border-white/10 bg-[#0e1015] p-3 text-xs space-y-1 font-mono">
                  <div className="flex justify-between text-slate-400">
                    <span>Taxable Value:</span>
                    <span>₹{computedTaxable.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-cyan-300">
                    <span>Total Tax (CGST + SGST + IGST):</span>
                    <span>₹{computedTotalTax.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-white text-sm pt-1 border-t border-white/10">
                    <span>Invoice Grand Total:</span>
                    <span>₹{computedGrandTotal.toFixed(2)}</span>
                  </div>
                </div>

                {/* Submit / Cancel Actions */}
                <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-white/10">
                  <button
                    type="button"
                    onClick={() => {
                      setIsManualModalOpen(false);
                      setManualFile(null);
                      setManualPreviewUrl('');
                    }}
                    disabled={isManualSaving}
                    className="px-4 py-2 rounded-xl border border-white/10 bg-white/5 text-slate-300 hover:bg-white/10 text-xs font-semibold transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveManualIngestion}
                    disabled={isManualSaving}
                    className="flex items-center gap-1.5 px-5 py-2 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-black font-bold text-xs transition shadow-md shadow-cyan-500/25"
                  >
                    <Save className="h-3.5 w-3.5" />
                    <span>{isManualSaving ? 'Saving & Auditing...' : 'Save & Link Document Proof'}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
